// Unpublished Work © 2024 Deere & Company.
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Modal, Typography } from "@mui/material";
import { models } from "powerbi-client";
import { PowerBIEmbed } from "powerbi-client-react";
import React from "react";

interface PowerBIModalProps {
  showModal: boolean;
  handleClose: () => void;
  embedUrl: string | null;
  accessToken: string | null;
  isLoading: boolean;
  title: string;
  onRetry?: () => void;
  filters?: any[];
  pageName?: string;
}

export const PowerBIModal: React.FC<PowerBIModalProps> = ({
  showModal,
  handleClose,
  embedUrl,
  accessToken,
  isLoading,
  title,
  onRetry,
  filters = [],
  pageName,
}) => (
  <Modal
    open={showModal}
    onClose={handleClose}
    aria-labelledby="powerbi-report-modal"
    aria-describedby="powerbi-report-content"
    style={{ zIndex: 900 }}
  >
    <Box
      sx={{
        position: "absolute",
        top: "5%",
        left: "5%",
        width: "90%",
        height: "90%",
        bgcolor: "background.paper",
        border: "2px solid #000",
        boxShadow: 24,
        p: 2,
        outline: "none",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <h2 data-testid="powerbi-report-modal">{title}</h2>
        <IconButton onClick={handleClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          height: "calc(100% - 82px)",
          "& .powerbi-report-container": { width: "100%", height: "100%" },
        }}
      >
        {isLoading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              sx={{ marginBottom: "30px" }}
            >
              Loading PowerBI Report
            </Typography>
            <Typography variant="body2" sx={{ color: "#666" }}>
              Fetching latest embed token
            </Typography>
          </Box>
        )}
        {!isLoading && (!embedUrl || !accessToken) && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h5"
              component="h2"
              sx={{ marginBottom: "30px" }}
            >
              PowerBI Report Unavailable
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "#666", textAlign: "center" }}
            >
              Unable to load PowerBI report.
              <br />
              Please check your connection and try again.
            </Typography>
            {onRetry && (
              <button
                type="button"
                onClick={onRetry}
                style={{
                  marginTop: "20px",
                  padding: "10px 20px",
                  backgroundColor: "#0078d4",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                }}
              >
                Retry
              </button>
            )}
          </Box>
        )}
        {!isLoading && embedUrl && accessToken && (
          <PowerBIEmbed
            embedConfig={{
              type: "report",
              embedUrl: embedUrl as string,
              accessToken: accessToken as string,
              tokenType: models.TokenType.Embed,
              ...(pageName && { pageName }),
              ...(filters.length > 0 && { filters }),
              settings: {
                navContentPaneEnabled: true,
                panes: {
                  filters: {
                    expanded: false,
                    visible: false,
                  },
                },
              },
            }}
            eventHandlers={
              new Map([
                ["error", function errorHandler() {}],
                ["loaded", function loadedHandler() {}],
                ["rendered", function renderedHandler() {}],
              ])
            }
            cssClassName="powerbi-report-container"
            getEmbeddedComponent={(embeddedReport) => {
              (window as any).report = embeddedReport;
            }}
          />
        )}
      </Box>
    </Box>
  </Modal>
);
